/**
 * @fileoverview Calculate mathematical expressions safely
 *
 * Parses and calculates simple mathematical expressions containing
 * addition and subtraction operations. Does NOT use eval() for security.
 *
 * @module utils/calculateExpression
 */

/**
 * Calculate a mathematical expression containing +, -, and numbers
 *
 * @param {string} input - The expression to calculate (e.g., "125+35-10")
 * @returns {Object} Result object with isValid, result, and error properties
 *
 * @example
 * calculateExpression("125+35") // { isValid: true, result: 160 }
 * calculateExpression("100-25+10") // { isValid: true, result: 85 }
 * calculateExpression("125.53+7.25") // { isValid: true, result: 132.78 }
 * calculateExpression("125++") // { isValid: false, error: "Invalid expression" }
 */
export function calculateExpression(input) {
    // Return error if input is empty or not a string
    if (!input || typeof input !== 'string') {
        return { isValid: false, error: 'Expression cannot be empty' };
    }

    // Strip commas and trim whitespace
    const expression = input.replace(/,/g, '').trim();

    // Validate: only allow numbers, +, -, ., and spaces
    const validChars = /^[\d\s+.\-]+$/;
    if (!validChars.test(expression)) {
        return { isValid: false, error: 'Only numbers, +, -, and . are allowed' };
    }

    // Check if it's just a plain number (no operators)
    // Use a stricter regex to ensure the entire string is a valid number
    const plainNumberRegex = /^-?\d+\.?\d*$/;
    if (plainNumberRegex.test(expression)) {
        const plainNumber = parseFloat(expression);
        return { isValid: true, result: plainNumber };
    }

    // Check for invalid patterns
    // - Multiple consecutive operators: ++, --, +-, -+
    // - Starting with an operator (except -)
    // - Ending with an operator
    // - Multiple decimal points in a number
    if (/[+\-]{2,}/.test(expression)) {
        return { isValid: false, error: 'Invalid operator sequence' };
    }
    if (/^[+]/.test(expression)) {
        return { isValid: false, error: 'Expression cannot start with +' };
    }
    if (/[+\-]$/.test(expression)) {
        return { isValid: false, error: 'Expression cannot end with an operator' };
    }

    try {
        // Split expression into tokens (numbers and operators)
        // This regex keeps the operators in the result
        const tokens = expression.split(/([+\-])/).filter(token => token.trim() !== '');

        // Start with the first number
        let result = parseFloat(tokens[0]);
        if (isNaN(result)) {
            return { isValid: false, error: 'Invalid number format' };
        }

        // Process remaining tokens in pairs (operator, number)
        for (let i = 1; i < tokens.length; i += 2) {
            const operator = tokens[i].trim();
            const nextNumber = parseFloat(tokens[i + 1]);

            if (isNaN(nextNumber)) {
                return { isValid: false, error: 'Invalid number format' };
            }

            if (operator === '+') {
                result += nextNumber;
            } else if (operator === '-') {
                result -= nextNumber;
            } else {
                return { isValid: false, error: 'Invalid operator' };
            }
        }

        // Check if result is a valid number
        if (isNaN(result) || !isFinite(result)) {
            return { isValid: false, error: 'Calculation resulted in invalid number' };
        }

        return { isValid: true, result: result };
    } catch (error) {
        return { isValid: false, error: 'Failed to calculate expression' };
    }
}
